import { LitElement, html } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { Chart } from 'highcharts';
import type {
	ChartOptions,
	TitleOptions,
	CreditsOptions,
	LegendOptions,
	TooltipOptions,
	XAxisOptions,
	YAxisOptions,
	YAxisPlotLinesOptions,
	PlotOptions,
	LangOptions,
	SeriesOptionsType,
	SeriesSplineOptions,
	ResponsiveOptions,
} from 'highcharts';

export type DateString = string;
type ScoreItem = number | string;
type GraphDataPoint = [number | Date, ScoreItem];

export type Threshold = {
	from: number;
	to: number;
	color: string;
	label?: string;
	severity: {
		title: string;
		description: string;
	};
};

export interface ChartSeriesData extends SeriesSplineOptions {
	data: GraphDataPoint[];
	name: string;
	description: string;
	color: string;
	icon?: string;
	chartType: 'spline' | 'line';
	thresholds: Threshold[];
	minScore: number;
	maxScore: number;
}

export enum XAxisStyle {
	DEFAULT,
	MONTH,
}

export const ONE_DAY_IN_MS = 24 * 3600 * 1000;
export const MAX_DAYS_IN_MONTH = 31;
export const DAYS_IN_WEEK = 7;

const CHEVRON_DOWN = html`<svg
	xmlns="http://www.w3.org/2000/svg"
	width="16"
	height="16"
	fill="currentColor"
	class="bi bi-chevron-down"
	viewBox="0 0 16 16"
>
	<path
		fill-rule="evenodd"
		d="M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708"
	/>
</svg>`;

const CHEVRON_UP = html`<svg
	xmlns="http://www.w3.org/2000/svg"
	width="16"
	height="16"
	fill="currentColor"
	class="bi bi-chevron-up"
	viewBox="0 0 16 16"
>
	<path
		fill-rule="evenodd"
		d="M7.646 4.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1-.708.708L8 5.707l-5.646 5.647a.5.5 0 0 1-.708-.708z"
	/>
</svg>`;

@customElement('chart-element')
export class ChartElement extends LitElement {
	@state()
	chart?: Chart;

	@state()
	error = false;

	@property({ type: String })
	locale: 'en-CA' | 'en-US' | 'es-US' = 'en-US';

	@property({ type: Array })
	chartSeriesData: ChartSeriesData[] = [];

	@property({ attribute: false })
	xAxisStyle: XAxisStyle = XAxisStyle.DEFAULT;

	// Enable the population health labels (Less Symptoms/More Symptoms instead of numbers)
	@property({ type: Boolean })
	enableSymptomsLabels = false;

	@property({ type: Boolean })
	showTooltips = false;

	@property({ type: Boolean })
	showStartingPoint = false;

	@property({ type: Boolean })
	showCustomLegendIcons = false;

	@state()
	_isRevealOpen = false;

	@query('#chart-container')
	chartContainerElement!: HTMLDivElement;

	// To allow Bootstrap css
	createRenderRoot() {
		return this;
	}

	handleRevealClick() {
		this._isRevealOpen = !this._isRevealOpen;
	}

	thrivingTemplate() {
		const label =
			this.chartSeriesData[0].thresholds.find(
				(t) =>
					t.to === this.chartSeriesData[0].maxScore ||
					t.from === this.chartSeriesData[0].maxScore
			)?.label || '';

		if (label) {
			return html`<div
				style="display: flex; justify-content: center; margin-bottom: 8px;"
			>
				<div
					style="font-size: 12px; background-color: #FFF3CD; padding: 8px 24px; border-radius: 20px;"
				>
					${label}
				</div>
			</div>`;
		}
	}

	descriptionTemplate() {
		return html`
			<div class="mb-3" style="font-size: 1rem;">
				${this.chartSeriesData[0].description}
			</div>
		`;
	}

	scoringExplainedTemplate() {
		const maxScore = this.chartSeriesData[0].maxScore;

		return html`
			<p class="d-inline-flex gap-1">
				<click-tracker
					data-event-name="Click Scoring Explained Button"
					data-track-button_page_location="progress_page_chart"
					data-track-button_action="${this._isRevealOpen ? 'show' : 'hide'}"
				>
					<button
						class="btn btn-link d-flex align-items-center p-0"
						data-bs-toggle="collapse"
						data-bs-target="#scoreExplainedCollpase"
						aria-expanded="false"
						aria-controls="scoreExplainedCollpase"
						@click="${this.handleRevealClick}"
					>
						<span class="pe-2">Scoring Explained</span>

						${this._isRevealOpen ? CHEVRON_UP : CHEVRON_DOWN}
					</button>
				</click-tracker>
			</p>
			<div class="collapse" id="scoreExplainedCollpase">
				<div class="table-responsive">
					<table class="table table-bordered" style="font-size: 12px;">
						<tbody>
							${this.chartSeriesData[0].thresholds.map((threshold) => {
								return html`
									<tr>
										<td class="text-nowrap pe-5">
											${`${threshold.from}-${
												threshold.to === maxScore ? maxScore : threshold.to - 1
											}`}
										</td>
										<td>${threshold.severity.title}</td>
										<td>${threshold.severity.description}</td>
									</tr>
								`;
							})}
						</tbody>
					</table>
				</div>
			</div>
		`;
	}

	errorTemplate() {
		return html`<div style="color: red; font-size: 1rem;">
			Error initializing chart
		</div>`;
	}

	protected render() {
		if (this.error) {
			return this.errorTemplate();
		}

		return html`
			${this.thrivingTemplate()}
			<div id="chart-container"></div>
			${this.descriptionTemplate()} ${this.scoringExplainedTemplate()}
		`;
	}

	// Called after the component's DOM has been updated the first time, immediately before updated() is called.
	protected firstUpdated() {
		this.initializeChart();
	}

	// Called whenever the component’s update finishes and the element's DOM has been updated and rendered.
	protected updated(changedProperties: Map<string | number | symbol, unknown>) {
		if (changedProperties.has('chartSeriesData')) {
			// TODO: Look into Highcharts events to re-render with new data
			this.chart?.destroy();
			this.chart = undefined;
			this.initializeChart();
		}
	}

	private initializeChart() {
		try {
			// Create chart
			this.chart = new Chart(
				this.chartContainerElement,
				this.getChartOptions()
			);
		} catch (error) {
			this.error = true;
			console.error('Error initializing chart:', error);
		}
	}

	private getChartOptions() {
		const locale = this.locale;
		const maxScore = this.chartSeriesData[0].maxScore;

		// Chart configuration
		const chart: ChartOptions = {
			type: this.chartSeriesData[0].chartType,
			style: {
				fontFamily: 'Matter SQ',
			},
			backgroundColor: 'transparent', // White by default
			marginTop: 20,
		};

		// Title
		const title: TitleOptions = {
			text: undefined,
		};

		// Credits
		const credits: CreditsOptions = {
			enabled: false,
		};

		// Legend
		const legend: LegendOptions = {
			align: 'left',
			verticalAlign: 'bottom',
			itemStyle: {
				fontWeight: '600',
				fontSize: '16px',
			},
			symbolHeight: 0,
			symbolWidth: 24,
		};

		// Tooltip
		const tooltip: TooltipOptions = {
			enabled: this.showTooltips,
			...(this.showTooltips && {
				backgroundColor: 'var(--bs-gray-900)',
				borderRadius: 16,
				style: {
					color: 'var(--bs-white)',
					fontSize: '14px',
					fontWeight: '600',
				},
				shadow: false,
				formatter: function () {
					const point = this.y;
					const thresholds = (this.series.userOptions as ChartSeriesData)
						.thresholds;
					let severity;

					if ((point === 0 || !!point) && thresholds.length > 0) {
						const threshold = thresholds.find(
							(threshold) =>
								point >= threshold.from &&
								(point < threshold.to || point === maxScore)
						);

						severity = threshold?.severity;
					}

					return `<div style="display: flex; flex-direction: column; gap: 8px; min-width: 200px; padding: 8px;">
						<div style="display: flex; align-items: center; gap: 4px;">
							<div style="width: 16px; height: 3px; background-color: ${
								this.series.color
							}"></div>
							${this.series.name}
						</div>
						<div style="font-size: 13px; font-weight: 400; color: var(--bs-gray-300);">${new Date(
							this.x
						).toLocaleDateString(locale, { dateStyle: 'medium' })}</div>
						${
							severity &&
							`<div style="font-size: 20px;">${severity.title}</div>
							<div style="font-size: 12px; font-weight: 400; color: var(--bs-gray-300); text-wrap: wrap; word-break: break-word;">
							${severity.description}
							</div>`
						}
					</div>`;
				},
				useHTML: true,
			}),
		};

		//  X Axis
		const xAxis: XAxisOptions = {
			labels: {
				style: {
					fontSize: '12px',
					fontWeight: '400',
					color: 'var(--bs-gray-900)',
				},
			},
			lineColor: 'var(--bs-gray-500)',
			lineWidth: 3,
			type: 'datetime',
			dateTimeLabelFormats: {
				day: '%b %e',
				week: '%b %e',
				month: '%b %e',
			},
			minTickInterval: ONE_DAY_IN_MS,
			tickLength: 0,
		};

		// Y Axis
		const yAxis: YAxisOptions = {
			title: {
				text: '',
			},
			labels: {
				style: {
					fontWeight: '400',
					fontSize: '12px',
					color: 'var(--bs-gray-900)',
				},
				formatter: (label) => {
					if (this.enableSymptomsLabels) {
						if (label.isFirst) {
							return (
								this.chartSeriesData[0].thresholds
									.find(
										(t) =>
											t.from === this.chartSeriesData[0].minScore ||
											t.to === this.chartSeriesData[0].minScore
									)
									?.severity.title.replaceAll(' ', '<br>') || 'Min'
							);
						}
						if (label.isLast) {
							return (
								this.chartSeriesData[0].thresholds
									.find(
										(t) =>
											t.to === this.chartSeriesData[0].maxScore ||
											t.from === this.chartSeriesData[0].maxScore
									)
									?.severity.title.replaceAll(' ', '<br>') || 'Max'
							);
						}
						return '';
					}

					return label.value.toString();
				},
			},
			lineColor: 'var(--bs-gray-500)',
			lineWidth: 3,
			min: this.chartSeriesData[0].minScore,
			max: this.chartSeriesData[0].maxScore,
			startOnTick: this.chartSeriesData[0].minScore === 0,
			gridLineWidth: 0,

			// Custom tick interval
			...(this.chartSeriesData[0].minScore !== 0 && {
				tickInterval: Math.abs(
					this.chartSeriesData[0].maxScore /
						(this.chartSeriesData[0].maxScore /
							this.chartSeriesData[0].minScore)
				),
			}),

			// Thresholds
			...(this.chartSeriesData[0].thresholds.length > 0 && {
				plotBands: this.chartSeriesData[0].thresholds.map((threshold) => {
					return {
						from: threshold.from,
						to: threshold.to,
						color: threshold.color,
						zIndex: 1,
					};
				}),
			}),

			// Starting Point
			...(this.showStartingPoint && {
				plotLines: this.chartSeriesData.map(
					(category: ChartSeriesData): YAxisPlotLinesOptions => {
						const categoryColor = `color-mix(in srgb, ${
							category.color || 'var(--bs-gray-900)'
						}, var(--bs-gray-900))`;

						return {
							value: category.data[0][1],
							color: categoryColor,
							dashStyle: 'LongDash',
							width: 1,
							label: {
								text: 'Starting Point',
								style: {
									color: categoryColor,
								},
								align: 'right',
								x: -10,
							},
							zIndex: 4,
						};
					}
				),
			}),
		};

		// Plot options
		const plotOptions: PlotOptions = {
			spline: {
				dashStyle: 'Solid',
				stickyTracking: true,
				lineWidth: 3,
			},
			line: {
				dashStyle: 'Solid',
				stickyTracking: true,
				lineWidth: 3,
			},
			area: {
				fillOpacity: 0.15,
			},
			series: {
				marker: {
					symbol: 'circle',
					radius: 3,
				},
			},
		};

		const lang: LangOptions = {
			shortMonths: [
				'Jan',
				'Feb',
				'Mar',
				'Apr',
				'May',
				'Jun',
				'Jul',
				'Aug',
				'Sep',
				'Oct',
				'Nov',
				'Dec',
			],
		};

		const series: SeriesOptionsType[] =
			this.chartSeriesData.map((data) => {
				return {
					...data,
					zIndex: 4,
				};
			}) || [];

		// Custom Legend Icon
		if (this.showCustomLegendIcons) {
			const iconSize = 10;
			this.chartSeriesData.forEach((category: ChartSeriesData) => {
				if (category.icon) {
					category.marker = {
						symbol: `url(${category.icon})`,
						width: iconSize,
						height: iconSize,
					};
				}
			});
		}

		const responsive: ResponsiveOptions = {
			rules: [
				{
					condition: {
						maxWidth: 500,
					},
					chartOptions: {
						yAxis: {
							labels: {
								formatter: (label) => {
									if (this.enableSymptomsLabels) {
										if (label.isFirst) {
											const title = this.chartSeriesData[0].thresholds.find(
												(t) =>
													t.from === this.chartSeriesData[0].minScore ||
													t.to === this.chartSeriesData[0].minScore
											)?.severity.title;

											if (title) {
												const index = title.search(/\p{Emoji}/gu);
												const emoji = title.substring(index);

												return emoji;
											}

											return 'Min';
										}

										if (label.isLast) {
											const title = this.chartSeriesData[0].thresholds.find(
												(t) =>
													t.to === this.chartSeriesData[0].maxScore ||
													t.from === this.chartSeriesData[0].maxScore
											)?.severity.title;

											if (title) {
												const index = title.search(/\p{Emoji}/gu);
												const emoji = title.substring(index);

												return emoji;
											}

											return 'Max';
										}
										return '';
									}

									return label.value.toString();
								},
							},
						},
					},
				},
			],
		};

		return {
			chart,
			title,
			credits,
			legend,
			tooltip,
			xAxis,
			yAxis,
			plotOptions,
			lang,
			series,
			responsive,
		};
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'chart-element': ChartElement;
	}
}
